/**
 * Login Page - Notion-Inspired Design
 * Business owner authentication with centered card layout
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, Input, Checkbox, Card } from '../components/ui';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, error, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setIsLoading(true);

    try {
      await login({ email, password, userType: 'BUSINESS' });
      navigate('/dashboard');
    } catch (err) {
      // Error is handled by context
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream-50 px-4">
      <div className="w-full max-w-md">
        {/* Logo/Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-orange-400 mb-4">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h1 className="text-h1 text-black font-bold">Buffables</h1>
          <p className="text-base text-black-lighter mt-2">Business Dashboard</p>
        </div>

        {/* Login Card */}
        <Card>
          <div className="p-8">
            <h2 className="text-h3 text-black font-semibold mb-2">Sign in to your account</h2>
            <p className="text-base text-black-lighter mb-6">
              Manage your loyalty program and reward your customers
            </p>

            {error && (
              <div className="mb-5 p-4 rounded-md bg-error-50 border border-error-200 flex items-start gap-3">
                <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-base text-error-700 font-medium">Network Error</p>
                  <p className="text-sm text-error-600 mt-1">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <Input
                type="email"
                id="email"
                label="Email address"
                placeholder="you@business.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                isRequired
                autoComplete="email"
              />

              <Input
                type="password"
                id="password"
                label="Password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                isRequired
                autoComplete="current-password"
              />

              <div className="flex items-center justify-between">
                <Checkbox label="Remember me" />
                <Link
                  to="/forgot-password"
                  className="text-base text-orange-400 hover:text-orange-300 font-medium transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                className="w-full"
              >
                Sign in
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-base text-black-lighter">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="text-orange-400 hover:text-orange-300 font-semibold transition-colors"
                >
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-black-lighter">
            © 2025 Buffables. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};
